import { action, computed, makeAutoObservable, runInAction } from 'mobx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import router from 'next/router';
import {
  AvailabilityType,
  CartProductAvailabilityType,
  CartProductPricesType,
  CartProductSizeType,
  ICartItem,
  ProductType,
  ICategoriesProduct,
  IElements,
  TUnitType,
  TQuantityType,
} from '@features/cart/typescript';
import NextCheckoutApi from '@features/api/nextApiService/nextCheckoutApi';
import addBusinessDays from '@utils/addBusinessDays';
import getProductUrl from '@utils/getProductUrl';
import { resolveOrderUnit, resolvePriceUnit } from '@utils/salesUnit';

dayjs.extend(relativeTime);

export default class CartProductState {
  public isProductLoading = false;

  public cartStoreReference;

  public onChange: VoidFunction;

  public cartId = '';

  public quantity: number;

  public unitType: TUnitType;

  public quantityType: TQuantityType;

  public multiplication: string;

  public orderUnit: string;

  public priceUnit: string;

  public isDip = false;

  public productId: string;

  public position: string;

  public productName: string;

  public url: string;

  public image: string;

  public prices: CartProductPricesType;

  public availability: CartProductAvailabilityType;

  public productSize: CartProductSizeType;

  public productType: ProductType;

  public productSubtype: ProductType;

  public productDescription: string | null;

  public size: CartProductSizeType;

  public alreadyConfirmedUnavailabilityQuantity = false;

  public alertReducedQuantity = false;

  public categories: Array<ICategoriesProduct>;

  public isAssemblable: boolean;

  public isBringable: boolean;

  constructor(
    onChange: VoidFunction,
    cartId: string,
    cartItem: ICartItem,
    cartStoreReference,
    alreadyConfirmedUnavailabilityQuantity: boolean,
  ) {
    makeAutoObservable(this, {
      showProductQuantityLimitedMessage: computed,
    });

    this.onChange = action(onChange);
    this.cartId = cartId;
    this.productName = JSON.parse(cartItem.configuration.json).productName;
    this.productId = JSON.parse(cartItem.configuration.json).productId;
    this.productDescription = cartItem.configuration.description;
    this.position = cartItem.positionId;
    this.quantity = cartItem.quantity;
    this.quantityType = cartItem.quantityType;
    this.unitType = cartItem.unitType;
    this.multiplication = cartItem.multiplication;
    this.orderUnit = resolveOrderUnit(cartItem.orderUnit, cartItem.quantityType);
    this.priceUnit = resolvePriceUnit(cartItem.priceUnit, cartItem.unitType, cartItem.orderUnit, cartItem.quantityType);
    this.isDip = this.unitType === 'square_meters' && this.quantityType === 'package';
    this.image = cartItem.image;
    this.prices = cartItem.prices;
    this.availability = cartItem.availability;
    this.productSize = cartItem.size;
    this.productType = cartItem.productType;
    this.productSubtype = cartItem.productSubtype;
    this.size = cartItem.size;
    this.cartStoreReference = cartStoreReference;
    this.alreadyConfirmedUnavailabilityQuantity = alreadyConfirmedUnavailabilityQuantity;
    this.categories = cartItem.categories;
    this.isAssemblable = cartItem.isAssemblable;
    this.isBringable = cartItem.isBringable;
    this.url = this.buildProductUrl(
      cartItem.parentName,
      cartItem.configuration.pimParentProductId,
      JSON.parse(cartItem.configuration.json).type,
      JSON.parse(cartItem.configuration.json).elements,
    );
    // this.routerReference = router;
  }

  public changeQuantity = action((qty: number) => {
    if (this.quantity !== qty) {
      void this.changeProductQuantity(qty);
    }
  });

  public increase = action(() => {
    void this.changeProductQuantity(this.quantity + 1);
  });

  public decrease = action(() => {
    void this.changeProductQuantity(this.quantity - 1);
  });

  private buildProductUrl = (
    productName: string,
    productId: number | string,
    productType: string,
    elements?: IElements,
  ) => {
    const productUrl = getProductUrl(productName, productId);
    switch (productType.toUpperCase()) {
      case 'CREATIVE':
      case 'UPHOLSTERY':
      case 'STOR':
      case 'SIMPLE':
        return `${productUrl}?objectUuid=${elements ? elements[0].productId : ''}`;
      case 'BASIC':
        return `${productUrl}?cart=${elements ? elements[0].productId : ''}`;
      default:
        return productUrl;
    }
  };

  private changeProductQuantity = async (quantity: number) => {
    runInAction(() => {
      this.isProductLoading = true;
    });

    const prevQuantity = this.quantity;
    try {
      await NextCheckoutApi.changePositionQuantity(this.cartId, this.position, quantity);

      runInAction(() => {
        this.quantity = quantity;
      });

      this.onChange();
    } catch (error) {
      runInAction(() => {
        this.quantity = prevQuantity;
      });
      throw error;
    } finally {
      runInAction(() => {
        setTimeout(() => {
          this.isProductLoading = false;
        }, 500);
      });
    }
  };

  public removeProduct = async () => {
    this.isProductLoading = true;
    try {
      await NextCheckoutApi.deletePosition(this.cartId, this.position);

      this.onChange();
    } catch (error) {
      runInAction(() => {
        this.isProductLoading = false;
      });
      throw error;
    } finally {
      runInAction(() => {
        setTimeout(() => {
          this.isProductLoading = false;
        }, 500);
      });
    }
  };

  public get showProductQuantityLimitedMessage() {
    return this.availability.amount > 0 && this.availability.amount < this.quantity;
  }

  public get showProductUnavailableMessage() {
    return this.availability.status === AvailabilityType.unavailable;
  }

  public getDelivery = () => this.cartStoreReference?.getLatestDeliveryForPosition(this.position) ?? 0;

  public get dateFrom() {
    const { amount, status, dateFrom } = this.availability;
    if (amount > 0 || status === AvailabilityType.unavailable) {
      return null;
    }

    const parsedDate = dateFrom.replace('UTC', '');
    const currentDayTime = dayjs(new Date().setHours(0, 0, 0, 0));
    if (router && router.locale)
      return addBusinessDays(this.getDelivery(), dayjs(parsedDate)).diff(currentDayTime, 'd');
    return null;
  }
}
